import { isDocRef, WithRef } from '@principle-theorem/shared';
import { TransactionAllocation } from './transaction-allocation';
import { OrganisationCache } from '../organisation/organisation-cache';
import {
  IInvoice,
  IPractice,
  IStaffer,
  SpecialTransactionAllocationTarget,
} from '@principle-theorem/principle-core/interfaces';
import { toAccountDetails } from './account-details';

export class BillingEntity {
  static async getBillingEntityForInvoice(
    invoice: IInvoice
  ): Promise<Pick<IInvoice, 'from' | 'attributedTo'> | undefined> {
    const practice = await OrganisationCache.practices.getDoc(
      invoice.practice.ref
    );
    const attributedTo =
      TransactionAllocation.getPrimaryAllocationTarget(invoice);

    if (
      !attributedTo ||
      attributedTo === SpecialTransactionAllocationTarget.Unallocated
    ) {
      const billingEntity = practice.billingEntities?.find(
        (entity) => entity.isDefault
      );

      if (!billingEntity) {
        return;
      }

      return {
        from: toAccountDetails({ practice, billingEntity }),
        attributedTo: {
          ref: practice.ref,
          billingEntityUid: billingEntity.uid,
        },
      };
    }

    if (isDocRef(attributedTo)) {
      const attributedToPractitioner =
        await OrganisationCache.staff.get.getDoc(attributedTo);
      const billingEntity = attributedToPractitioner.billingEntities?.find(
        (entity) => entity.isDefault
      );

      if (!billingEntity) {
        return;
      }

      return {
        from: toAccountDetails({
          staffer: attributedToPractitioner,
          billingEntity,
        }),
        attributedTo: {
          ref: attributedToPractitioner.ref,
          billingEntityUid: billingEntity.uid,
        },
      };
    }
  }

  static entitiesForPractice(
    practice: WithRef<IPractice>
  ): Pick<IInvoice, 'from' | 'attributedTo'>[] {
    return (practice.billingEntities ?? [])
      .filter(
        (entity) => !!entity.displayName && !!entity.entity.registrationNumber
      )
      .map((billingEntity) => ({
        from: toAccountDetails({ practice, billingEntity }),
        attributedTo: {
          ref: practice.ref,
          billingEntityUid: billingEntity.uid,
        },
      }));
  }

  static entitiesForStaffer(
    staffer: WithRef<IStaffer>
  ): Pick<IInvoice, 'from' | 'attributedTo'>[] {
    return (staffer.billingEntities ?? [])
      .filter(
        (entity) => !!entity.displayName && !!entity.entity.registrationNumber
      )
      .map((billingEntity) => ({
        from: toAccountDetails({ staffer, billingEntity }),
        attributedTo: {
          ref: staffer.ref,
          billingEntityUid: billingEntity.uid,
        },
      }));
  }
}
